<script lang="ts">
  // Sperrbildschirm fuer den gesperrten Vault-Bereich (Slice F8,
  // docs/vault-sync.md).
  //
  // Gemeinsame Komponente, weil inzwischen ZWEI Seiten dahinter liegen
  // (Vault-Browser und Entgelt-Auswertung). Eine zweite Kopie waere
  // genau die Sorte Duplikat, die auseinanderlaeuft -- und bei einer
  // Entsperr-Oberflaeche will man nicht zwei Wahrheiten darueber haben,
  // wann jemand hineinsehen darf.
  //
  // Einzelne Ziffernfelder statt Passwort-Input: sie sagen ohne Text,
  // wie viele Stellen erwartet werden, und werfen auf dem iPhone direkt
  // die Zifferntastatur. Darunter liegt EIN echtes Input -- das behaelt
  // Fokus, Einfuegen und Tastatur-Support.

  import Icon from "./Icon.svelte";
  import { t } from "./i18n.svelte";
  import { csrfHeader } from "./api";

  interface Props {
    // Wird nach erfolgreicher Eingabe gerufen -- die Seite laedt dann
    // ihre Daten neu.
    onunlocked: () => void | Promise<void>;
    title?: string;
    hint?: string;
  }

  let { onunlocked, title, hint }: Props = $props();

  let pinInput = $state("");
  let pinError = $state("");
  let verifying = $state(false);
  let pinEl = $state<HTMLInputElement | null>(null);

  // Sperrbildschirm heisst: die einzige sinnvolle Handlung ist tippen.
  // Also gehoert der Fokus dorthin, sobald das Gate erscheint.
  $effect(() => {
    if (pinEl) pinEl.focus();
  });

  async function submitPin() {
    if (pinInput.length < 4) {
      pinError = t("vault.browse.pin_format", "PIN braucht 4–6 Ziffern.");
      return;
    }
    pinError = "";
    verifying = true;
    try {
      const r = await fetch("/v2-api/vault/pin/verify", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...csrfHeader() },
        body: JSON.stringify({ pin: pinInput }),
      });
      if (r.status === 200) {
        pinInput = "";
        await onunlocked();
        return;
      }
      if (r.status === 429) {
        pinError = t("vault.browse.pin_lockout", "Zu viele Fehlversuche — kurz Pause.");
        pinInput = "";
      } else if (r.status === 401) {
        pinError = t("vault.browse.pin_wrong", "PIN falsch.");
        pinInput = "";
      } else {
        pinError = `HTTP ${r.status}`;
      }
    } catch (e) {
      pinError = e instanceof Error ? e.message : String(e);
    } finally {
      verifying = false;
    }
  }

  // Nur Ziffern, hoechstens 6. Bei voller Laenge direkt absenden --
  // ein Extra-Klick auf "Entsperren" waere hier reine Zeremonie.
  function onPinInput(e: Event) {
    const el = e.currentTarget as HTMLInputElement;
    const cleaned = el.value.replace(/\D/g, "").slice(0, 6);
    pinInput = cleaned;
    el.value = cleaned;
    pinError = "";
    if (cleaned.length === 6 && !verifying) void submitPin();
  }
</script>

<div class="grid place-items-center py-10">
  <div class="border-border surface flex w-full max-w-sm flex-col items-center gap-3 rounded-xl border p-6 text-center">
    <span class="text-accent"><Icon name="lock" size={26} /></span>
    <div>
      <div class="text-fg t-headline">
        {title ?? t("vault.browse.pin_prompt_title", "Vault gesperrt")}
      </div>
      <div class="text-fg-subtle t-meta mt-0.5">
        {hint ?? t("vault.browse.pin_prompt_hint", "PIN eingeben — bleibt 30 Minuten offen")}
      </div>
    </div>

    <div class="pinwrap" class:shake={pinError !== ""}>
      <input
        bind:this={pinEl}
        type="text"
        inputmode="numeric"
        autocomplete="one-time-code"
        maxlength="6"
        value={pinInput}
        oninput={onPinInput}
        onkeydown={(e) => e.key === "Enter" && submitPin()}
        disabled={verifying}
        class="pin-input"
        aria-label={t("vault.browse.pin_aria", "PIN, 4 bis 6 Ziffern")}
      />
      {#each [0, 1, 2, 3, 4, 5] as i (i)}
        <span
          class="pinbox"
          class:filled={i < pinInput.length}
          class:cur={i === pinInput.length}
          class:opt={i >= 4}
          aria-hidden="true"
        >{i < pinInput.length ? "•" : ""}</span>
      {/each}
    </div>

    {#if pinError}
      <p class="text-danger t-meta">{pinError}</p>
    {:else}
      <p class="text-fg-subtle t-meta">
        {t("vault.browse.pin_lockout_hint", "Nach 5 Fehlversuchen 15 Minuten Pause.")}
      </p>
    {/if}

    <button
      type="button"
      class="bg-accent text-accent-fg rounded-md px-4 py-2 text-sm font-medium disabled:opacity-50"
      disabled={verifying || pinInput.length < 4}
      onclick={submitPin}
    >
      {verifying ? "…" : t("vault.browse.pin_submit", "Entsperren")}
    </button>
  </div>
</div>

<style>
  .pinwrap {
    position: relative;
    display: flex;
    gap: 0.5rem;
  }
  .pin-input {
    position: absolute;
    inset: 0;
    width: 100%;
    opacity: 0;
    cursor: text;
  }
  .pinbox {
    display: grid;
    place-items: center;
    width: 2.1rem;
    height: 2.6rem;
    border: 1px solid var(--border);
    border-radius: 0.5rem;
    background: var(--bg);
    color: var(--fg-muted);
    font-size: 1.15rem;
    transition:
      border-color 120ms,
      box-shadow 120ms;
  }
  /* Stelle 5 und 6 sind optional (PIN ist 4-6 Ziffern) -- sie stehen
     leiser da, damit die Pflichtlaenge ablesbar bleibt. */
  .pinbox.opt {
    border-style: dashed;
  }
  .pinbox.filled {
    border-color: var(--marke);
    color: var(--fg);
  }
  .pinwrap:focus-within .pinbox.cur {
    border-color: var(--marke);
    box-shadow: 0 0 0 3px color-mix(in oklch, var(--marke) 16%, transparent);
  }
  .shake {
    animation: shake 320ms;
  }
  @keyframes shake {
    0%,
    100% {
      transform: translateX(0);
    }
    25% {
      transform: translateX(-4px);
    }
    75% {
      transform: translateX(4px);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .shake {
      animation: none;
    }
  }
</style>
